import { WrappedColumnHeader } from "components/common/dataGrid/WrappedColumnHeader";
import { useCallback, useMemo, useState } from "react";
import { useTenant } from "hooks/settings";
import { LayeringPreview } from "components/common/plan/layerEditor/useLayeringPreview";
import { useTenantData } from "contexts/tenant";
import { MixLayeringDialog } from "components/common/plan/layerEditor/MixLayeringDialog";
import { Box } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridColumnGroup,
  GridFilterModel,
} from "@mui/x-data-grid-premium";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UndoIcon from "@mui/icons-material/Undo";
import { EditableMixTable } from "components/common/plan/EditableMixTable";
import { simpleSteelGrades } from "components/common/plan/columns";
import { Row } from "components/common/plan/types";
import { useTenantTranslation } from "hooks/formatters";
import { sorted } from "helpers";
import {
  FixedMixCreate,
  FixedMixImpurities,
  FixedMixRead,
  MaterialRead,
  ParameterConflict,
  ProductionMixResultRead,
  SteelGrade,
  usePreviewLayeringMutation,
} from "store/api/generatedApi";
import { ConflictMarkerDot } from "./ConflictMarkerDot";
import { MarkerDot } from "./MarkerDot";
import { fixedMixMatches } from "./fixedMixEdits";
import { ConflictReasons } from "./ConflictReasons";
import {
  applyFixedMixRows,
  applyFixedMixLayers,
  fixedMixResult,
  fixedMixRows,
  FixedMixGroup,
  fixedMixDraftsMatch,
  restoreFixedMixGroup,
  revertFixedMixGrades,
} from "./fixedMixRows";

import { FixedMixGradeCell } from "./FixedMixGradeCell";
import { SplitFixedMixButton } from "./SplitFixedMixButton";

const EMPTY_ROWS: Row[] = [];
const COLUMN_GROUPS: GridColumnGroup[] = [];
const FILTER_MODEL: GridFilterModel = { items: [] };
const COLUMN_VISIBILITY = {
  mix_name: false,
  num_heats: false,
  grouping: false,
};

export const FixedMixDefaultsTable = ({
  mixes,
  savedMixes,
  savedMixGroups,
  groups,
  chemistry,
  chefGroupNameById,
  limitLabelBySignature,
  onSplit,
  source,
  scrapchef,
  materials,
  steelGrades,
  basketNumbers,
  selectedGrades,
  conflicts,
  disabled,
  loading,
  onChange,
  onRevert,
  onDiscard,
}: {
  mixes: FixedMixCreate[];
  savedMixes: FixedMixRead[];
  savedMixGroups: string[][];
  groups: FixedMixGroup[];
  chemistry: FixedMixImpurities[];
  chefGroupNameById: Record<number, string>;
  limitLabelBySignature: Record<string, string>;
  onSplit: (gradeNames: string[]) => void;
  source: FixedMixCreate[] | undefined;
  scrapchef: FixedMixCreate[] | undefined;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
  basketNumbers: number[];
  selectedGrades: string[];
  conflicts: ParameterConflict[];
  disabled: boolean;
  loading: boolean;
  onChange: (mixes: FixedMixCreate[]) => void;
  onRevert: (gradeNames: string[]) => void;
  onDiscard: (gradeNames: string[]) => void;
}) => {
  const { t } = useTenantTranslation();
  const { layer_edit_mode } = useTenantData();
  const tenantName = useTenant();
  const [previewLayering] = usePreviewLayeringMutation();
  const previewAutomaticLayers = useCallback<LayeringPreview>(
    (mixCreate) => {
      const pending = previewLayering({ tenantName, mixCreate });
      return {
        response: pending.unwrap(),
        abort: () => {
          pending.abort();
        },
      };
    },
    [previewLayering, tenantName]
  );
  const [layerDialog, setLayerDialog] = useState<{
    mixResult: ProductionMixResultRead;
    gradeNames: string[];
    source: FixedMixRead[];
  } | null>(null);
  const [edited, setEdited] = useState<{
    source: FixedMixRead[];
    rows: Row[];
  } | null>(null);
  const editedRows = edited?.source === savedMixes ? edited.rows : EMPTY_ROWS;
  const rows = useMemo(
    () =>
      fixedMixRows({
        groups,
        mixes,
        steelGrades,
        selectedGrades,
        basketNumbers,
        editedRows,
      }),
    [groups, mixes, steelGrades, selectedGrades, basketNumbers, editedRows]
  );
  const visibleBasketMaterials = useMemo(
    () =>
      sorted(
        materials.filter(
          (material) =>
            material.visibility === "visible" &&
            material.addition_location === "basket"
        ),
        (material) => material.ordering
      ),
    [materials]
  );
  const basketMaterials = useMemo(
    () =>
      visibleBasketMaterials.map((material) => ({
        id: material.id,
        name: material.name,
        inventory: null,
      })),
    [visibleBasketMaterials]
  );
  const hopper = materials.find(
    (material) =>
      material.visibility === "visible" &&
      material.addition_location === "hopper"
  );
  const preloadedDris = useMemo(
    () =>
      Object.fromEntries(
        groups.map((group) => [
          group.mixId,
          mixes
            .find((recipe) =>
              group.gradeNames.includes(recipe.steel_grade_name)
            )
            ?.mix.furnace?.find(
              (material) => material.material_name === hopper?.name
            ) ?? null,
        ])
      ),
    [groups, mixes, hopper?.name]
  );
  const selectedSteelGradeIds = useMemo(
    () =>
      steelGrades
        .filter(
          (grade) =>
            selectedGrades.length === 0 || selectedGrades.includes(grade.name)
        )
        .map((grade) => grade.id),
    [steelGrades, selectedGrades]
  );
  const differs = useCallback(
    (gradeNames: string[], candidates: FixedMixCreate[] | undefined) =>
      candidates !== undefined &&
      gradeNames.some((name) => {
        const recipe = mixes.find((recipe) => recipe.steel_grade_name === name);
        const candidate = candidates.find(
          (candidate) => candidate.steel_grade_name === name
        );
        return (
          recipe !== undefined &&
          (candidate === undefined ||
            !fixedMixMatches(recipe.mix, candidate.mix))
        );
      }),
    [mixes]
  );
  const layerEditing = useMemo(
    () =>
      layer_edit_mode === "none"
        ? undefined
        : {
            onEditLayers: (mixId: number) => {
              if (disabled) return;
              const group = groups.find((group) => group.mixId === mixId);
              if (!group) return;
              const mixResult = fixedMixResult({ group, mixes, basketNumbers });
              if (mixResult !== null)
                setLayerDialog({
                  mixResult,
                  gradeNames: group.gradeNames,
                  source: savedMixes,
                });
            },
            hasLayerEdits: (mixId: number) => {
              const group = groups.find((group) => group.mixId === mixId);
              return group !== undefined && differs(group.gradeNames, source);
            },
          },
    [
      layer_edit_mode,
      disabled,
      groups,
      mixes,
      basketNumbers,
      savedMixes,
      differs,
      source,
    ]
  );
  const activeLayerDialog =
    layer_edit_mode !== "none" &&
    !disabled &&
    layerDialog?.source === savedMixes
      ? layerDialog
      : null;
  const overrides = useMemo<GridColDef<Row>[]>(() => {
    const gradeColumn = simpleSteelGrades(t, 240, selectedSteelGradeIds);
    const mixGroups = groups.map((group) => group.gradeNames);
    return [
      {
        ...gradeColumn,
        renderCell: (params) => {
          if (
            params.row.type !== "profile" &&
            params.row.type !== "empty_profile"
          )
            return null;
          const gradeNames =
            groups.find((group) => group.mixId === params.row.mixId)
              ?.gradeNames ?? [];
          const flagged = conflicts.filter((conflict) =>
            conflict.steel_grade_names.some((name) => gradeNames.includes(name))
          );
          return (
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <FixedMixGradeCell
                steelGrades={params.row.steelGrades}
                selectedSteelGradeIds={selectedSteelGradeIds}
                chemistry={chemistry}
                loading={loading}
              />
              {!loading && differs(gradeNames, scrapchef) ? (
                <MarkerDot
                  corner="top-right"
                  color="secondary.main"
                  label={t("differsFromDefault")}
                  tooltip={t("differsFromDefault")}
                />
              ) : null}
              <ConflictMarkerDot
                corner="top-left"
                conflicts={flagged}
                tooltip={
                  <ConflictReasons
                    conflicts={flagged}
                    chefGroupNameById={chefGroupNameById}
                    limitLabelBySignature={limitLabelBySignature}
                  />
                }
              />
            </Box>
          );
        },
      },
      ...[
        {
          field: "discard",
          label: "discardUnsavedChanges",
          width: 220,
          renderAction: (gradeNames: string[]) => {
            if (disabled || source === undefined) return null;
            const discarded = restoreFixedMixGroup({
              mixes,
              mixGroups,
              sourceMixes: source,
              sourceGroups: savedMixGroups,
              gradeNames,
            });
            if (fixedMixDraftsMatch({ mixes, mixGroups }, discarded))
              return null;
            return (
              <GridActionsCellItem
                key="discard"
                icon={<UndoIcon />}
                label={t("discardUnsavedChanges")}
                onClick={() => {
                  setEdited(null);
                  onDiscard(gradeNames);
                }}
              />
            );
          },
        },
        {
          field: "restore",
          label: "restoreScrapchefDefaults",
          width: 220,
          renderAction: (gradeNames: string[]) => {
            const reverted =
              scrapchef === undefined
                ? null
                : revertFixedMixGrades({
                    mixes,
                    mixGroups,
                    defaults: scrapchef,
                    gradeNames,
                  });
            if (
              disabled ||
              loading ||
              reverted === null ||
              fixedMixDraftsMatch({ mixes, mixGroups }, reverted)
            )
              return null;
            return (
              <GridActionsCellItem
                key="restore"
                icon={<RestartAltIcon />}
                label={t("restoreScrapchefDefaults")}
                onClick={() => {
                  setEdited(null);
                  onRevert(gradeNames);
                }}
              />
            );
          },
        },
        {
          field: "split",
          label: "splitMix",
          width: 100,
          renderAction: (gradeNames: string[]) =>
            disabled || gradeNames.length < 2 ? null : (
              <SplitFixedMixButton
                key="split"
                gradeNames={gradeNames}
                disabled={false}
                onSplit={onSplit}
              />
            ),
        },
      ].map(({ field, label, width, renderAction }): GridColDef<Row> => ({
        field,
        type: "actions",
        headerName: t(label),
        renderHeader: () => <WrappedColumnHeader label={t(label)} />,
        width,
        rowSpanValueGetter: (_, row) => row.mixId,
        getActions: ({ row }) => {
          if (row.type !== "profile" && row.type !== "empty_profile") return [];
          const gradeNames =
            groups.find((group) => group.mixId === row.mixId)?.gradeNames ?? [];
          const action = renderAction(gradeNames);
          return action === null ? [] : [action];
        },
      })),
    ];
  }, [
    t,
    selectedSteelGradeIds,
    groups,
    conflicts,
    chemistry,
    loading,
    differs,
    scrapchef,
    chefGroupNameById,
    limitLabelBySignature,
    disabled,
    source,
    mixes,
    savedMixGroups,
    onDiscard,
    onRevert,
    onSplit,
  ]);
  return (
    <>
      <EditableMixTable
        rows={rows}
        materials={basketMaterials}
        selectedSteelGradeIds={selectedSteelGradeIds}
        disableEdit={disabled}
        hasEdited={groups.some((group) => differs(group.gradeNames, source))}
        onChange={(rows) => {
          setEdited({ source: savedMixes, rows });
          onChange(applyFixedMixRows({ groups, mixes, rows }));
        }}
        mixGrouping="grouping_name"
        hasDri={hopper !== undefined}
        steelGradesWidth={240}
        showSummary={false}
        preloadedDris={preloadedDris}
        columnGroupingModel={COLUMN_GROUPS}
        filterModel={FILTER_MODEL}
        columnVisibilityModel={COLUMN_VISIBILITY}
        columnOverrides={overrides}
        loading={loading}
        layerEditing={layerEditing}
      />
      {activeLayerDialog !== null ? (
        <MixLayeringDialog
          open
          previewLayering={previewAutomaticLayers}
          mixResult={activeLayerDialog.mixResult}
          materials={visibleBasketMaterials}
          onApply={(update) => {
            const updatedMix = update.new_mix_production.mix;
            if (updatedMix === null || updatedMix === undefined) return;
            setEdited(null);
            onChange(
              applyFixedMixLayers({
                gradeNames: activeLayerDialog.gradeNames,
                mixes,
                baskets: updatedMix.baskets,
              })
            );
          }}
          onClose={() => {
            setLayerDialog(null);
          }}
        />
      ) : null}
    </>
  );
};
